import React from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import { useSelector } from "react-redux";
import formatDate from "../../../utils/formatDate";

function VitalCard({ 
  title, 
  value, 
  unit, 
  status, 
  statusColor, 
  statusBg, 
  data, 
  color, 
  lines,
  darkMode = false,
  minValue,
  maxValue,
  averageValue,
  isRTL = false,
  graphMode = "month" // Add graphMode prop
}) {
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const isArabic = defaultLanguage === "ar";
  
  // Chart theme colors
  const chartColors = {
    grid: darkMode ? "#374151" : "#F3F4F6",
    axis: darkMode ? "#6B7280" : "#9CA3AF",
    tooltipBg: darkMode ? "#1F2937" : "#FFFFFF",
    tooltipBorder: darkMode ? "#374151" : "#E5E7EB",
    tooltipText: darkMode ? "#F3F4F6" : "#111827",
  };

  // Helper function to format value with unit
  const formatValue = (val) => {
    if (val === undefined || val === null) return '--';
    return `${val}${unit}`;
  };

  // Determine if we should use RTL
  const rtl = isRTL || isArabic;

  // Get the appropriate direction for the chart
  const chartDirection = rtl ? "rtl" : "ltr";

  // Custom X-axis tick formatter for month names
  const formatXAxisTick = (value) => {
    // If graphMode is 'year', convert number (1-12) to month name
    if (graphMode === 'year') {
      const monthNumber = parseInt(value);
      if (monthNumber >= 1 && monthNumber <= 12) {
        // Create a date with the month (using 0-indexed month)
        const date = new Date(2000, monthNumber - 1, 1);
        // Use the current language for month names
        const locale = defaultLanguage === "fr" ? "fr-FR" : defaultLanguage === "ar" ? "ar-MA" : "en-US";
        return date.toLocaleString(locale, { month: 'short' });
      }
    }
    // For month mode or other cases, return the value as is
    return value;
  };

  // Custom tooltip formatter for translations
  const customTooltipFormatter = (value, name) => {
    // Translate the label names
    const translatedName = textTranslator(name?.toLowerCase() || "", "VISITS");
    
    // Format the value with unit if needed
    const formattedValue = `${value} ${unit}`;
    
    return [formattedValue, translatedName || name];
  };

  // Custom tooltip label formatter
  const customLabelFormatter = (label) => {
    // If graphMode is 'year', convert number to month name in tooltip
    if (graphMode === 'year') {
      const monthNumber = parseInt(label);
      if (monthNumber >= 1 && monthNumber <= 12) {
        const date = new Date(2000, monthNumber - 1, 1);
        const locale = defaultLanguage === "fr" ? "fr-FR" : defaultLanguage === "ar" ? "ar-MA" : "en-US";
        return date.toLocaleString(locale, { month: 'long' });
      }
    }
    return label;
  };

  return (
    <div 
      className={`rounded-xl border p-4 md:p-5
                  ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-100'}`}
      dir={chartDirection}
    >
      {/* header row */}
      <div className={`flex items-center justify-between mb-1 ${arClass(rtl, "flex-row-reverse")}`}>
        <div className={`flex items-center gap-2.5 ${arClass(rtl, "flex-row-reverse")}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center
                          ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
            <Heart className={`w-3.5 h-3.5`} style={{ color: color }} />
          </div>
          <span className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {title}
          </span>
        </div>
        <div className={`flex items-center gap-2 ${arClass(rtl, "flex-row-reverse")}`}>
          <span className={`text-lg font-extrabold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {value}
          </span>
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {unit}
          </span>
          <span
            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full`}
            style={{ background: statusBg, color: statusColor }}
          >
            {status}
          </span>
        </div>
      </div>

      {/* stats row - min, max, average */}
      <div className={`flex items-center gap-4 mt-1 mb-2 ${arClass(rtl, "flex-row-reverse")}`}>
        <div className={`flex items-center gap-1 ${arClass(rtl, "flex-row-reverse")}`}>
          <span className={`text-[10px] font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {textTranslator("min", "VISITS")}:
          </span>
          <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {formatValue(minValue)}
          </span>
        </div>
        <div className={`flex items-center gap-1 ${arClass(rtl, "flex-row-reverse")}`}>
          <span className={`text-[10px] font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {textTranslator("max", "VISITS")}:
          </span>
          <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {formatValue(maxValue)}
          </span>
        </div>
        <div className={`flex items-center gap-1 ${arClass(rtl, "flex-row-reverse")}`}>
          <span className={`text-[10px] font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {textTranslator("avg", "VISITS")}:
          </span>
          <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {formatValue(averageValue)}
          </span>
        </div>
      </div>

      {/* chart */}
      <div className="h-[300px] mt-1" dir={chartDirection}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data} 
            margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis 
              dataKey="x"
              tick={{ fontSize: 10, fill: chartColors.axis }} 
              tickLine={false} 
              axisLine={false} 
              reversed={rtl}
              tickFormatter={formatXAxisTick}
            />
            <YAxis 
              tick={{ fontSize: 10, fill: chartColors.axis }} 
              tickLine={false} 
              axisLine={false} 
              orientation={rtl ? "right" : "left"}
            />
            <Tooltip 
              contentStyle={{ 
                fontSize: 12, 
                borderRadius: 8, 
                border: `1px solid ${chartColors.tooltipBorder}`,
                background: chartColors.tooltipBg,
                color: chartColors.tooltipText,
              }}
              formatter={customTooltipFormatter}
              labelFormatter={customLabelFormatter}
            />
            {lines ? lines.map(l => (
              <Line 
                key={l.key} 
                type="monotone" 
                dataKey={l.key} 
                stroke={l.stroke} 
                strokeWidth={2} 
                dot={{ r: 3, fill: l.stroke }} 
                strokeDasharray={l.dash} 
                name={textTranslator(l.name, "VISITS")}
              />
            )) : (
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={color} 
                strokeWidth={2} 
                dot={{ r: 3, fill: color }} 
                name={textTranslator("value", "VISITS")}
              />
            )}
            {lines && (
              <Legend 
                iconType="circle" 
                iconSize={8} 
                wrapperStyle={{ 
                  fontSize: 10, 
                  paddingTop: 4,
                  color: darkMode ? '#9CA3AF' : '#6B7280'
                }} 
                align={rtl ? "right" : "left"}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default VitalCard;